import Hammer from "hammerjs";

import { DomUtil } from "@/shared/ui/Helper";

export abstract class BasePopover {
    public readonly uniqueId: string | undefined;

    private static $dim?: JQuery;
    private static instances = new Array<BasePopover>();

    protected $element: JQuery;
    protected $head: JQuery;
    protected $body: JQuery;

    private $parent: JQuery;

    private touchCtrl: HammerManager;

    private dragOffset = 0;
    private hideTreshold = 180;
    private overdrag = 30;
    private isClosing = false;
    private isMovingDown = false;
    protected fullscreen = true;
    private contentHeight = 0;

    public onBecameVisible?: { () };
    public onDestroyed?: { () };
    public onClosed?: { () };
    private hideIfOthersAppear = false;

    constructor(title?: string, fullscreen?: boolean, uniqueId?: string, flexHeight?: boolean, condensed?: boolean) {
        this.$parent = $(".app");
        this.uniqueId = uniqueId;

        if (fullscreen !== undefined) this.fullscreen = fullscreen;
        if (flexHeight !== undefined) {
            this.fullscreen = false;
        }

        this.$element = $('<div class="popover" />')
            .addClass(this.fullscreen ? "fullscreen" : "")
            .addClass(flexHeight ? "flex-height" : "")
            .addClass(condensed ? "condensed" : "");

        if (title !== undefined) {
            this.$head = $('<div class="head dragable"><div class="title">' + title + "</div></div>").appendTo(
                this.$element
            );
        } else this.$head = $('<div class="head dragable"><div class="handle" /></div>').appendTo(this.$element);

        //add close indicator
        $('<div class="closer"><i class="fal fa-times"></i></div>')
            .prependTo(this.$head)
            .click((e) => {
                e.preventDefault();
                this.hide();
            });

        this.$body = $('<div class="body" />').appendTo(this.$element);
        if (flexHeight) {
            this.$body.addClass("scrollable-element");
        }

        //bind head closing
        this.touchCtrl = new Hammer(this.$head[0], {
            recognizers: [
                [Hammer.Pan, { direction: Hammer.DIRECTION_VERTICAL, threshold: 0 }],
                [Hammer.Swipe, { direction: Hammer.DIRECTION_VERTICAL, threshold: 20 }],
            ],
            domEvents: false,
        });

        this.touchCtrl.on("panstart", () => {
            this.dragOffset = DomUtil.translatePosition(this.$element).y;
            this.$element.addClass("notransition");
            BasePopover.$dim?.addClass("notransition");
            this.isClosing = false;
        });

        this.touchCtrl.on("panmove", (ev) => {
            if (ev.direction === 8) this.isMovingDown = false;
            else if (ev.direction === 16) this.isMovingDown = true;

            this.movePosition(ev.deltaY);
        });

        this.touchCtrl.on("panend", () => {
            if (this.isClosing) return;

            this.isClosing = true;
            this.releasePosition();
        });

        this.touchCtrl.on("swipedown", () => {
            if (!this.isMovingDown) return;

            if (this.isClosing) return;

            this.isClosing = true;
            this.hide();
        });

        BasePopover.instances.push(this);
    }

    public show() {
        //dim background
        if (BasePopover.$dim === undefined) {
            BasePopover.$dim = $('<div class="dim" />').appendTo(this.$parent);
            BasePopover.$dim[0].offsetHeight;
            BasePopover.$dim.addClass("shown");
        } else BasePopover.$dim.stop(true, false);

        BasePopover.$dim.addClass("shown");
        BasePopover.$dim.unbind("click").bind("click", (e) => {
            e.preventDefault();
            this.hide();
        });

        if (this.$element.parent().length === 0) {
            this.$element.appendTo(this.$parent);

            if (!this.fullscreen) {
                let childrenHeight = 0;
                this.$body.children().each((i, e) => {
                    childrenHeight += $(e).outerHeight(true);
                });

                this.contentHeight = childrenHeight + this.$head.height() + this.overdrag;
                const maxHeight = $(window).height() - 40 + this.overdrag;
                if (this.contentHeight > maxHeight) {
                    this.contentHeight = maxHeight;
                    this.$element.find("> .body").css("padding-bottom", this.overdrag);
                }

                this.$element.height(this.contentHeight);
            }

            this.$element[0].offsetHeight;
        } else {
            this.$element.show()[0].offsetHeight;
        }

        this.$element.addClass("shown");
        this.onShow();

        if (this.onBecameVisible != null) {
            this.onBecameVisible();
        }

        for (const i in BasePopover.instances) {
            const inst = BasePopover.instances[i];
            if (inst.hideIfOthersAppear === true && !inst.$element.is(this.$element)) {
                inst.hide();
            }
        }
    }

    public hide() {
        this.dragOffset = 0;

        //check if no other popover is appended
        if (this.$element.siblings(".popover:visible").length === 0) {
            BasePopover.$dim
                ?.removeClass("shown notransition")
                .stop(true, false)
                .delay(350)
                .queue((a) => {
                    BasePopover.$dim!.remove();
                    BasePopover.$dim = undefined;

                    a();
                });
        }

        this.$element
            .removeClass("shown notransition")
            .css("transform", "")
            .delay(350)
            .queue((e) => {
                this.$element.hide();

                this.onHide();
                if (this.onClosed !== undefined) this.onClosed();

                e();
            });
    }

    public destroy() {
        this.touchCtrl.destroy();
        this.$element.remove();

        this.onDestroy();

        const idx = BasePopover.instances.indexOf(this);
        if (idx !== -1) BasePopover.instances.splice(idx, 1);

        if (this.onDestroyed !== undefined) this.onDestroyed();
    }

    public updateBodyHeight(useAnimation?: boolean) {
        if (this.fullscreen) {
            this.$element.css("height", "auto");
            return;
        }

        if (useAnimation === undefined) useAnimation = true;

        let childrenHeight = 0;
        this.$body.children().each((i, e) => {
            childrenHeight += $(e).outerHeight(true);
        });

        this.contentHeight = childrenHeight + this.$head.height() + this.overdrag;
        const maxHeight = $(window).height() - 40 + this.overdrag;
        if (this.contentHeight > maxHeight) {
            this.contentHeight = maxHeight;
            this.$element.find("> .body").css("padding-bottom", this.overdrag);
        }

        if (useAnimation) this.$element.stop(true, false).animate({ height: this.contentHeight }, 1000, "easeOutExpo");
        else this.$element.height(this.contentHeight);
    }

    public setFullScreen(isFullscreen: boolean): BasePopover {
        this.fullscreen = isFullscreen;
        if (this.fullscreen) this.$element.addClass("fullscreen");
        else this.$element.removeClass("fullscreen");

        this.updateBodyHeight(false);

        return this;
    }

    public setTitle(title?: string): BasePopover {
        const existingTitle = this.$head.find("> .title");
        const existingCloser = this.$head.find("> .closer");

        if (title !== undefined) {
            if (existingTitle.length > 0) existingTitle.html(title);
            else this.$head.append('<div class="title">' + title + "</div>");

            if (existingCloser.length === 0) {
                $('<div class="closer"><i class="fal fa-times"></i></div>')
                    .prependTo(this.$head)
                    .click((e) => {
                        e.preventDefault();
                        this.hide();
                    });
            }
        } else {
            if (existingTitle.length !== 0) existingTitle.remove();

            if (existingCloser.length !== 0) existingCloser.remove();
        }

        return this;
    }

    public static hasCurrentlyVisiblePopover(uniqueId: string): boolean {
        if (uniqueId == null) {
            return false;
        } else {
            for (const i in BasePopover.instances) {
                const instance = BasePopover.instances[i];
                if (instance.$element.hasClass("shown") && instance.uniqueId === uniqueId) {
                    return true;
                }
            }

            return false;
        }
    }

    public static hasCurrentlyVisiblePopovers(): boolean {
        for (const i in BasePopover.instances) {
            if (BasePopover.instances[i].$element.hasClass("shown")) return true;
        }

        return false;
    }

    public static hideVisiblePopovers() {
        for (const i in BasePopover.instances) {
            if (BasePopover.instances[i].$element.hasClass("shown")) {
                BasePopover.instances[i].hide();
            }
        }
    }

    public setHideIfOtherAppears(active: boolean) {
        this.hideIfOthersAppear = active;
    }

    private releasePosition() {
        if (BasePopover.$dim !== undefined && BasePopover.$dim.length != 0) {
            const b = BasePopover.$dim.removeClass("notransition")[0];
            b.offsetHeight;

            const a = this.$element.removeClass("notransition")[0];
            a.offsetHeight;

            BasePopover.$dim.removeAttr("style");
        }

        const tollerance = this.fullscreen ? 150 : this.contentHeight * 0.2;
        if (DomUtil.translatePosition(this.$element).y > tollerance && this.isMovingDown) this.hide();
        else this.$element.css("transform", "");
    }

    private movePosition(delta: number) {
        let newPos = this.dragOffset + delta;
        if (newPos <= 0) {
            if (!this.fullscreen) newPos += DomUtil.momentumResistance(newPos, this.overdrag);
            else newPos = 0;
        }

        let c = 1 - (0.5 / this.hideTreshold) * newPos;
        if (c > 1) c = 1;
        else if (c < 0) c = 0;

        BasePopover.$dim!.css("opacity", c);
        this.$element.css({ transform: "translate3d(0, " + newPos + "px, 0)" });
    }

    protected abstract onShow();
    protected abstract onHide();
    protected abstract onDestroy();
}
